
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, Paper, Alert } from '@mui/material';
import { requestPasswordReset } from '../api/authApi';
import useAuthStore from '../../../store/authStore';

const ForgotPasswordForm: React.FC = () => {
  const { isLoading, error } = useAuthStore();
  
  // Form state
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Validate email
  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('Email is required');
      return false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Email is invalid');
      return false;
    }
    
    setEmailError('');
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateEmail()) {
      return;
    }
    
    try {
      await requestPasswordReset(email);
      setSubmitted(true);
    } catch (error) {
      // Error is handled in the API layer and stored in the auth store
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Reset Password
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      {submitted ? (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            If an account exists with that email, we've sent password reset instructions.
          </Alert>
          
          <Typography variant="body2" gutterBottom>
            Please check your email for further instructions.
          </Typography>
          
          <Box textAlign="center" mt={3}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Button variant="contained" color="primary">
                Return to Login
              </Button>
            </Link>
          </Box>
        </Box>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="body1" gutterBottom>
            Enter your email address and we'll send you instructions to reset your password.
          </Typography>
          
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!emailError}
            helperText={emailError}
            disabled={isLoading}
          />
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Send Reset Instructions'}
          </Button>
          
          <Box textAlign="center" mt={2}>
            <Link to="/login" style={{ textDecoration: 'none' }}>
              <Typography variant="body2" color="primary">
                Back to Login
              </Typography>
            </Link>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ForgotPasswordForm;
